import ALLOWED_TYPES from './AllowedTypes';
import logger from '../../lib/logger';
import { ContentType } from '../../constants/mediaFormatProfiles';

const MAX_DURATION_HOURS = 3;
const MAX_DURATION_SECONDS = MAX_DURATION_HOURS * 3600;

interface FileValidationResult {
  isValid: boolean;
  errorMessage?: string;
  errorType?: 'FILE_TYPE' | 'DURATION' | 'LOADING_ERROR';
  /** If the file needs to be converted in server side */
  needsConversion?: boolean;
  file?: File;
}

const getFileDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    let mediaElement: HTMLAudioElement | HTMLVideoElement;

    if (file.type.startsWith('audio')) {
      mediaElement = new Audio();
    } else if (file.type.startsWith('video') || file.name.endsWith('.mkv')) {
      mediaElement = document.createElement('video');
    } else {
      reject(new Error('Unsupported media type'));
      return;
    }

    const objectURL = URL.createObjectURL(file);

    mediaElement.src = objectURL;

    mediaElement.onloadedmetadata = () => {
      URL.revokeObjectURL(objectURL);
      resolve(mediaElement.duration);
    };

    mediaElement.onerror = () => {
      URL.revokeObjectURL(objectURL);
      reject(new Error('Error loading media file'));
    };
  });
};

const validateFileUpload = async (
  file: File
): Promise<FileValidationResult> => {
  // Check if file type is allowed
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  const fileTypeAllowed =
    ALLOWED_TYPES.includes(file.type as ContentType) ||
    (fileExtension &&
      ALLOWED_TYPES.includes(`.${fileExtension}` as ContentType));

  if (!fileTypeAllowed) {
    const errorMessage = `"${file.name}" cannot be uploaded. You can upload video files (MP4, MOV, etc.) or audio files (MP3, WAV, etc.).`;
    logger.error(new Error(errorMessage));
    return {
      isValid: false,
      errorMessage,
      errorType: 'FILE_TYPE',
    };
  }

  // Check if this is a WAV file
  const isWavFile =
    file.type === 'audio/wav' || file.name.toLowerCase().endsWith('.wav');

  try {
    const duration = await getFileDuration(file);
    if (duration > MAX_DURATION_SECONDS) {
      const errorMessage = `"${file.name}" is too long. Videos must be ${MAX_DURATION_HOURS} hours or shorter.`;
      logger.error(new Error(errorMessage));
      return {
        isValid: false,
        errorMessage,
        errorType: 'DURATION',
      };
    }
  } catch (error) {
    logger.error('File validation error:', error);

    if (isWavFile) {
      // For WAV files, if we can't get duration, mark for server-side processing
      console.log(
        `WAV file ${file.name} couldn't be decoded by the browser. Marking for server-side processing.`
      );
      return {
        isValid: true,
        needsConversion: true,
        file,
      };
    }

    const errorMessage = `Unable to process "${file.name}". The file might be corrupted or in an unsupported format.`;
    return {
      isValid: false,
      errorMessage,
      errorType: 'LOADING_ERROR',
    };
  }

  // For WAV files that we can read duration, still mark them for conversion
  if (isWavFile) {
    return {
      isValid: true,
      needsConversion: true,
      file,
    };
  }

  return { isValid: true };
};

export interface UploadValidationError {
  isValid: false;
  errorMessage?: string;
  errorType?: 'FILE_TYPE' | 'DURATION' | 'LOADING_ERROR';
}

interface UploadValidationNeedsConversion {
  isValid: true;
  filesNeedingConversion: File[];
}

const validateFiles = async (
  files: File[]
): Promise<UploadValidationError | UploadValidationNeedsConversion> => {
  const validationResults = await Promise.all(
    files.map((file) => validateFileUpload(file))
  );

  // Return the first error if any
  const firstError = validationResults.find((result) => !result.isValid);
  if (firstError) {
    return {
      isValid: false,
      errorMessage: firstError.errorMessage,
      errorType: firstError.errorType,
    };
  }

  const filesNeedingConversion = validationResults
    .filter((result) => result.needsConversion && result.file)
    .map((result) => result.file!);

  return {
    isValid: true,
    filesNeedingConversion,
  };
};

export default validateFiles;
